import dayjs from '../../utils/dayjs';
import { BlockStack, Button, Card, DataTable, EmptyState, Text } from '@shopify/polaris';
import Pagination from 'components/pagination';
import helpers from 'helpers/index';
import { ___ } from 'languages/index';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import emptyIMG from '../../media/images/empty.png';
import { ENUM_INVOICE_STATUS, TypedInvoices } from 'queries/invoices.query';
import { useAuth } from 'context/AuthContext';
import Capacity from 'components/Capacity';
import { FolderDownIcon } from '@shopify/polaris-icons';
import VehicleIncomeLogFilter from './filter.income.log';
import { useDownloadInvoicesViaVehicleID, useGetInvoicesViaVehicleID } from 'queries/trip_manifest.query';

interface Props {
  vehicle_id: string;
}

export default function IncomeLog({ vehicle_id }: Props) {
  const { currentUserCan } = useAuth();

  const history = useNavigate();
  const { search } = useLocation();
  const StringQuery: any = helpers.ExtractUrl(search);

  const initialQuery = useMemo(
    () => ({
      query: '',
      page: 1,
      limit: 25,
      sort: 'createdAt:desc',
      vehicle_id: vehicle_id /** xóa bỏaaaaa */,
    }),
    [vehicle_id]
  );

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });

  const [entities, setEntities] = useState<TypedInvoices[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const { data, refetch: getEntities, isFetching: loading } = useGetInvoicesViaVehicleID(mainQuery);

  useEffect(() => {
    if (data) {
      const allInvoices = data.body.flatMap((manifest) =>
        manifest.orders.map((order) => order.invoices).filter((invoice): invoice is NonNullable<typeof invoice> => invoice != null)
      );
      setEntities(allInvoices);
      setTotalItems(data.totalItems || 0);
    }
  }, [data]);

  useEffect(() => {
    setMainQuery((prev) => ({ ...prev, vehicle_id, page: 1 }));
  }, [vehicle_id]);

  useEffect(() => {
    const buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) {
      history(`/vehicle/${vehicle_id}/income` + buildURLSearch);
    }
    getEntities();
  }, [mainQuery]);

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prev) => ({ ...prev, page: numPage, limit }));
  }, []);

  const filterCallback = useCallback(
    (value: any) => {
      if (value === false) {
        setMainQuery(initialQuery);
        return;
      }
      setMainQuery((prev) => ({ ...prev, ...value, page: 1, vehicle_id }));
    },
    [initialQuery, vehicle_id]
  );

  const rows = entities?.map((invoices) => [
    invoices.invoice_id || '-',
    invoices.shDon || '-',
    invoices.maTraCuu || '-',
    invoices.TaxType || '-',
    helpers.formatNumberWithCommas(invoices.TgThTien || 0),
    helpers.formatNumberWithCommas(invoices.TgTThue || 0),
    helpers.formatNumberWithCommas(invoices.TgTTTBSo || 0),
    invoices.invoice_status === ENUM_INVOICE_STATUS.CANCEL ? 'Đã hủy' : invoices.is_signed === 1 ? 'Đã ký' : 'Nháp',
    invoices.createdAt ? dayjs(new Date(Number(invoices.createdAt))).format('HH:mm:ss DD-MM-YYYY') : '-',
  ]);

  /** TẢI XUỐNG */
  const { mutateAsync: downloadExcelThisFilter, isPending: downloadGenerating } = useDownloadInvoicesViaVehicleID();
  const generateDownloadLink = useCallback(
    async (vehicle_id: any) => {
      try {
        const blob = await downloadExcelThisFilter(mainQuery);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vehicle_log_${vehicle_id}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {}
    },
    [mainQuery]
  );

  const emptyData = (
    <EmptyState heading="Không có thu nhập!" image={emptyIMG}>
      <p>Không có dữ liệu phù hợp. Hãy thử bỏ bộ lọc hoặc tìm kiếm từ khóa khác.</p>
    </EmptyState>
  );

  return (
    <>
      <BlockStack gap="400">
        <Card padding="0">
          <VehicleIncomeLogFilter loading={loading} onCallback={filterCallback} />

          {entities?.length > 0 ? (
            <DataTable
              hoverable
              fixedFirstColumns={1}
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
              headings={['ID', 'Số hóa đơn', 'Mã tra cứu', 'Loại thuế', 'Tiền trước thuế', 'Tiền thuế', 'Tổng tiền', 'Trạng thái', 'Tạo']}
              rows={rows}
              hideScrollIndicator
              footerContent={___('Show page {display_page_number} of {total_record_number} results', {
                display_page_number: <Text as="strong">{mainQuery?.page || 1}</Text>,
                total_record_number: <Text as="strong">{totalItems}</Text>,
              })}
            />
          ) : (
            emptyData
          )}
        </Card>

        {totalItems > mainQuery.limit ? (
          <Pagination TotalRecord={totalItems} activeCurrentPage={mainQuery.page} pageSize={mainQuery.limit} onChange={onChangePagination} />
        ) : null}
      </BlockStack>
      <br />
      <br />
      <Capacity current_user_can={'download_vehicle_log'}>
        <Button icon={FolderDownIcon} variant="monochromePlain" onClick={() => generateDownloadLink(vehicle_id)}>
          Tải xuống XLSX bản này
        </Button>
      </Capacity>
      <br />
      <br />
    </>
  );
}

